import {
  equalsToText,
  errorWhenIsPathOnly,
  filterToRows,
  MAX_FILTER_ROWS,
  responseRuleErrors,
  responseRules,
} from './connectorResponseConfig';
import { authorityError, emptyTool, toolErrors, toolToBody, toolToForm } from './connectorToolConfig';

// The form's checks are conveniences: packages/connectors/http/validate.ts is the authority and
// its message is rendered as it arrives. Each rule here is one the server also enforces, so a
// config that passes this module still has to survive the write.
const PLACEHOLDER = /\{\{\s*([^}\s]+)\s*\}\}/g;

export const TIMEOUT_BOUNDS = { min: 500, max: 30000 };
export const RATE_BOUNDS = { min: 1, max: 6000 };
export { errorWhenIsPathOnly, MAX_FILTER_ROWS };

const DEFAULT_AUTH = { header: 'Authorization', prefix: 'Bearer ' };

// IDENTITY_PLACEHOLDERS in packages/connectors/http/validate.ts: the substituter fills all four
// from the resolved profile whatever the contract's identityFields declare, so the form takes the
// server's set and not the contract's.
export const IDENTITY_PLACEHOLDERS = ['identity.email', 'identity.externalId', 'identity.phone', 'identity.profileId'];

// SHOPPER_PLACEHOLDERS in packages/connectors/http/template.ts: filled from the run, '' when unknown.
export const SHOPPER_PLACEHOLDERS = ['shopper.lat', 'shopper.lng', 'shopper.city'];

// identity.externalId has no live source yet: the substituter renders it empty on every shopper
// call. It is still a placeholder the server accepts, so the form takes it — but a config keyed
// only on it keys on nothing, which is the same position as no identity at all.
export const UNRESOLVED_IDENTITY_PLACEHOLDERS = ['identity.externalId'];
export const RESOLVED_IDENTITY_PLACEHOLDERS = IDENTITY_PLACEHOLDERS.filter(
  (name) => !UNRESOLVED_IDENTITY_PLACEHOLDERS.includes(name)
);

// A digit marker parses both as a bare JSON number and inside a string, so a placeholder left in
// structure position still yields a document that parses and can be named (the server's trick).
const BODY_MARKER_BASE = 987654321000000;

export const placeholdersIn = (text) => [...String(text ?? '').matchAll(PLACEHOLDER)].map((match) => match[1]);

const objectToRows = (object) => Object.entries(object || {}).map(([key, value]) => ({ key, value: String(value) }));

const rowsToObject = (rows) =>
  (rows || []).reduce((object, row) => {
    const key = row.key.trim();
    if (key) object[key] = row.value.trim();
    return object;
  }, {});

const parseUrl = (text) => {
  try {
    return new URL(text);
  } catch {
    return null;
  }
};

export const emptyForm = () => ({
  mode: 'capability',
  capability: '',
  // The picker names one contract; an instance the API returns with more keeps the rest here so a
  // save of an unrelated field does not drop them.
  contractsTail: [],
  method: 'GET',
  url: '',
  headerRows: [],
  body: '',
  domainSecretId: null,
  authHeader: DEFAULT_AUTH.header,
  authPrefix: DEFAULT_AUTH.prefix,
  authIn: 'header',
  authParam: '',
  root: '',
  rootKind: 'array',
  mapRows: [],
  filterRows: [],
  notFoundPath: '',
  notFoundEquals: '',
  errorWhenPath: '',
  errorWhenMode: 'equals',
  errorWhenValue: '',
  // A rule authored here always compares against a value; only one that arrived from the server
  // without an equals or a notEquals sets this false. See errorWhenIsPathOnly.
  errorWhenHasComparison: true,
  timeoutMs: 5000,
  rateLimitPerMinute: 60,
  tool: emptyTool(),
});

export const toForm = (instance) => {
  if (!instance) return emptyForm();

  const config = instance.config || {};
  const request = config.request || {};
  const response = config.response || {};
  const notFoundWhen = response.notFoundWhen;
  const errorWhen = response.errorWhen;
  const errorWhenIsNotEquals = !!errorWhen && Object.prototype.hasOwnProperty.call(errorWhen, 'notEquals');
  const errorWhenHasComparison =
    !errorWhen || errorWhenIsNotEquals || Object.prototype.hasOwnProperty.call(errorWhen, 'equals');
  const blank = emptyForm();

  return {
    mode: instance.tool ? 'tool' : 'capability',
    capability: instance.contracts?.[0] || '',
    contractsTail: instance.contracts?.slice(1) || [],
    method: request.method || blank.method,
    url: request.url || '',
    headerRows: objectToRows(request.headers),
    body: request.body || '',
    domainSecretId: instance.domainSecretId ?? null,
    authHeader: config.auth?.header || DEFAULT_AUTH.header,
    authPrefix: config.auth?.prefix ?? DEFAULT_AUTH.prefix,
    authIn: config.auth?.in === 'query' ? 'query' : 'header',
    authParam: config.auth?.param || '',
    root: response.root || '',
    rootKind: response.rootKind || blank.rootKind,
    mapRows: objectToRows(response.map),
    filterRows: filterToRows(response.filter),
    notFoundPath: notFoundWhen?.path || '',
    notFoundEquals: equalsToText(notFoundWhen?.equals),
    errorWhenPath: errorWhen?.path || '',
    errorWhenMode: errorWhenIsNotEquals ? 'notEquals' : 'equals',
    errorWhenValue: errorWhen ? equalsToText(errorWhenIsNotEquals ? errorWhen.notEquals : errorWhen.equals) : '',
    errorWhenHasComparison,
    timeoutMs: config.timeoutMs ?? blank.timeoutMs,
    rateLimitPerMinute: config.rateLimitPerMinute ?? blank.rateLimitPerMinute,
    tool: toolToForm(instance.tool),
  };
};

export const toBody = (form) => {
  const request = { method: form.method, url: form.url.trim(), headers: rowsToObject(form.headerRows) };
  if (form.method === 'POST' && form.body.trim()) request.body = form.body;

  const response = { map: rowsToObject(form.mapRows) };
  if (form.root.trim()) response.root = form.root.trim();
  if (form.rootKind && form.rootKind !== 'array') response.rootKind = form.rootKind;
  Object.assign(response, responseRules(form));

  const auth = { header: form.authHeader.trim(), prefix: form.authPrefix };
  if (form.authIn === 'query') {
    auth.in = 'query';
    auth.param = form.authParam.trim();
  }

  const body = {
    type: 'http',
    contracts: form.mode === 'tool' ? [] : [form.capability, ...(form.contractsTail || [])],
    domainSecretId: form.domainSecretId ?? null,
    config: {
      request,
      auth,
      response,
      timeoutMs: form.timeoutMs,
      rateLimitPerMinute: form.rateLimitPerMinute,
    },
  };
  if (form.mode === 'tool') body.tool = toolToBody(form.tool);

  return body;
};

const collectStrings = (value, out) => {
  if (typeof value === 'string') out.push(value);
  else if (Array.isArray(value)) value.forEach((item) => collectStrings(item, out));
  else if (value && typeof value === 'object') Object.values(value).forEach((item) => collectStrings(item, out));
  return out;
};

const bodyJsonError = (body) => {
  const names = [];
  const marked = body.replace(PLACEHOLDER, (_match, name) => {
    names.push(name);
    return String(BODY_MARKER_BASE + names.length - 1);
  });

  let parsed;
  try {
    parsed = JSON.parse(marked);
  } catch {
    return 'The body is not valid JSON.';
  }

  const strings = collectStrings(parsed, []);
  const loose = names.findIndex((_, index) => !strings.some((text) => text.includes(String(BODY_MARKER_BASE + index))));
  return loose === -1 ? null : `{{${names[loose]}}} has to sit inside a JSON string, between quotes.`;
};

const duplicateKey = (rows) => {
  const seen = new Set();
  const repeated = (rows || []).find((row) => {
    const key = row.key.trim();
    if (!key) return false;
    if (seen.has(key)) return true;
    seen.add(key);
    return false;
  });
  return repeated ? repeated.key.trim() : null;
};

const inBounds = (value, bounds) => Number.isInteger(value) && value >= bounds.min && value <= bounds.max;

const LEGEND = [...IDENTITY_PLACEHOLDERS, ...SHOPPER_PLACEHOLDERS]
  .map((name) => `{{${name}}}`)
  .concat('{{args.<name>}}')
  .join(', ');

// argNames is null outside tool mode, where any args.<name> is accepted (the capability's own
// handler resolves it); in tool mode only a declared argument's placeholder is known.
const unknownIn = (text, argNames) =>
  placeholdersIn(text).find((name) => {
    if (IDENTITY_PLACEHOLDERS.includes(name) || SHOPPER_PLACEHOLDERS.includes(name)) return false;
    const argMatch = /^args\.([^.]+)$/.exec(name);
    if (!argMatch) return true;
    return argNames ? !argNames.includes(argMatch[1]) : false;
  });

const unknownMessage = (name) => `{{${name}}} is not a value the server can fill in. Use ${LEGEND}.`;

const unknownMessageIn = (text, argNames) => {
  const name = unknownIn(text, argNames);
  return name ? unknownMessage(name) : null;
};

export const formErrors = (form, contract) => {
  const errors = {};
  const isTool = form.mode === 'tool';
  // Outside tool mode there is no declared list, so any args.<name> is accepted as today; in tool
  // mode only a name the tool actually declares is a value the server can fill in.
  const argNames = isTool ? form.tool.argRows.map((row) => row.name.trim()).filter(Boolean) : null;

  if (!isTool && !form.capability) errors.capability = 'Pick the contract this API answers.';

  const url = form.url.trim();
  const parsed = parseUrl(url);
  const unknownUrl = unknownIn(form.url, argNames);
  if (!url) errors.url = 'A URL is required.';
  // A template leaves the host empty; the URL parser would read the first path segment as one.
  else if (/^https:\/\/\//i.test(url)) errors.url = 'Add the API host after https://.';
  else if (!parsed) errors.url = 'That is not a valid URL.';
  else if (parsed.protocol !== 'https:') errors.url = 'The URL has to start with https://.';
  else if (unknownUrl) errors.url = unknownMessage(unknownUrl);
  else if (isTool) {
    const authority = authorityError(form.url);
    if (authority) errors.url = authority;
  }

  const duplicateHeader = duplicateKey(form.headerRows);
  const unknownHeader = form.headerRows.map((row) => unknownIn(row.value, argNames)).find(Boolean);
  if (duplicateHeader) errors.headers = `${duplicateHeader} is set twice.`;
  else if (unknownHeader) errors.headers = unknownMessage(unknownHeader);

  if (form.domainSecretId && !form.authHeader.trim()) errors.authHeader = 'The secret needs a header to travel in.';
  if (form.authIn === 'query' && !form.authParam.trim()) errors.authParam = 'A query parameter name is required.';

  if (form.method === 'POST' && form.body.trim()) {
    const unknownBody = unknownIn(form.body, argNames);
    if (unknownBody) errors.body = unknownMessage(unknownBody);
    else {
      const malformed = bodyJsonError(form.body);
      if (malformed) errors.body = malformed;
    }
  }

  const duplicateField = duplicateKey(form.mapRows);
  // A store tool has no result contract: map keys are free, so only the shape checks below apply.
  const fields = isTool ? [] : contract?.resultFields || [];
  const mapped = form.mapRows.map((row) => row.key.trim()).filter(Boolean);
  const pathless = form.mapRows.find((row) => row.key.trim() && !row.value.trim());
  if (duplicateField) errors.map = `${duplicateField} is mapped twice.`;
  else if (pathless) errors.map = `${pathless.key.trim()} needs a response path.`;
  else if (fields.length) {
    const names = fields.map((field) => field.name);
    const stranger = mapped.find((key) => !names.includes(key));
    const missing = fields.find((field) => field.required && !mapped.includes(field.name));
    if (stranger) errors.map = `${stranger} is not a result field of ${form.capability}.`;
    else if (missing) errors.map = `${missing.name} is required by ${form.capability} and is not mapped.`;
  }

  Object.assign(
    errors,
    responseRuleErrors(form, (text) => unknownMessageIn(text, argNames))
  );

  if (!inBounds(form.timeoutMs, TIMEOUT_BOUNDS))
    errors.timeoutMs = `Between ${TIMEOUT_BOUNDS.min} and ${TIMEOUT_BOUNDS.max} milliseconds.`;
  if (!inBounds(form.rateLimitPerMinute, RATE_BOUNDS))
    errors.rateLimitPerMinute = `Between ${RATE_BOUNDS.min} and ${RATE_BOUNDS.max} calls a minute.`;

  if (isTool) {
    // Only a placeholder the substituter can actually fill counts: a personal tool keyed on
    // {{identity.externalId}} alone would present an arbitrary answer to every shopper as theirs.
    const hasIdentityPlaceholder = [...placeholdersIn(form.url), ...placeholdersIn(form.body)].some((name) =>
      RESOLVED_IDENTITY_PLACEHOLDERS.includes(name)
    );
    const toolFieldErrors = toolErrors(form.tool, { hasIdentityPlaceholder });
    // A key present with an empty value would still count as an error to the caller, which checks
    // Object.keys(errors).length to decide whether the form is clean.
    if (Object.keys(toolFieldErrors).length) errors.tool = toolFieldErrors;
  }

  return errors;
};
